import { motion, AnimatePresence } from "framer-motion";
import { X, Zap, Crown, AlertTriangle, ShoppingCart, ArrowRight, BookOpen, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * CreditsModal - Popup shown when user tries to perform an action without sufficient credits/limits
 * 
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onClose: function - Called when modal is closed
 * - blockingReason: string - "insufficient_credits" | "story_limit" | "character_limit"
 * - creditsRequired: number - Credits needed for the action
 * - creditsAvailable: number - User's current credit balance
 * - limitsData: object - { stories: { used, max, remaining }, characters: { used, max, remaining } }
 * - planName: string - Current plan name (e.g., "Free", "Starter")
 */
const CreditsModal = ({ 
  isOpen, 
  onClose, 
  blockingReason = "insufficient_credits",
  creditsRequired = 0,
  creditsAvailable = 0,
  limitsData = {},
  planName = "Free"
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate("/subscription");
  };

  const handleBuyCredits = () => {
    onClose();
    navigate("/subscription#credit-packs");
  };

  // Determine content based on blocking reason
  const getContent = () => {
    switch (blockingReason) {
      case "story_limit":
        return {
          icon: <BookOpen className="w-12 h-12 text-pink-500" />,
          title: "Story Limit Reached",
          description: `You've created ${limitsData.stories?.used || 0} out of ${limitsData.stories?.max || 0} stories this month on your ${planName} plan.`,
          suggestion: "Upgrade your plan to create more magical stories!",
          primaryAction: { label: "Upgrade Plan", action: handleUpgrade, icon: <Crown className="w-4 h-4" /> },
          secondaryAction: null
        };
      
      case "character_limit":
        return {
          icon: <Users className="w-12 h-12 text-amber-500" />,
          title: "Character Limit Reached",
          description: `You have ${limitsData.characters?.used || 0} out of ${limitsData.characters?.max || 0} characters on your ${planName} plan.`,
          suggestion: "Upgrade to create more custom characters for your stories!",
          primaryAction: { label: "Upgrade Plan", action: handleUpgrade, icon: <Crown className="w-4 h-4" /> },
          secondaryAction: null
        };
      
      case "insufficient_credits":
      default:
        return {
          icon: <Zap className="w-12 h-12 text-violet-500" />,
          title: "Not Enough Credits",
          description: `This action requires ${creditsRequired} credits, but you only have ${creditsAvailable} credits available.`,
          suggestion: "Add more credits or upgrade your plan to continue creating!",
          primaryAction: { label: "Buy Credits", action: handleBuyCredits, icon: <ShoppingCart className="w-4 h-4" /> },
          secondaryAction: { label: "Upgrade Plan", action: handleUpgrade, icon: <Crown className="w-4 h-4" /> }
        };
    }
  };

  const content = getContent();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient */}
            <div className="bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 p-6 text-center relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="w-20 h-20 bg-white/20 rounded-full mx-auto flex items-center justify-center mb-4 backdrop-blur-sm">
                {content.icon}
              </div>
              
              <h2 className="text-2xl font-bold text-white">{content.title}</h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100 mb-6">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-700 text-sm">{content.description}</p>
                  <p className="text-slate-500 text-sm mt-2">{content.suggestion}</p>
                </div>
              </div>

              {/* Current Status */}
              <div className="flex items-center justify-center gap-6 mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{creditsAvailable}</p>
                  <p className="text-xs text-slate-500">Credits Available</p>
                </div>
                {creditsRequired > 0 && (
                  <>
                    <div className="text-slate-300">
                      <ArrowRight className="w-5 h-5" />
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-violet-600">{creditsRequired}</p>
                      <p className="text-xs text-slate-500">Credits Needed</p>
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={content.primaryAction.action}
                  className="w-full py-3 px-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-200"
                >
                  {content.primaryAction.icon}
                  {content.primaryAction.label}
                </button>
                
                {content.secondaryAction && (
                  <button
                    onClick={content.secondaryAction.action}
                    className="w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
                  >
                    {content.secondaryAction.icon}
                    {content.secondaryAction.label}
                  </button>
                )}
                
                <button
                  onClick={onClose}
                  className="w-full py-2 text-slate-500 hover:text-slate-700 text-sm transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreditsModal;
